
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { useBoardContext } from '@/contexts/BoardContext';
import { BoardPresenceProvider, useBoardPresence } from '@/contexts/BoardPresenceContext';
import { CardPresenceProvider } from '@/contexts/OptimizedCardPresenceContext';
import { DragStateProvider, useDragStateContext } from '@/contexts/DragStateContext';
import { DragGhostCard } from '@/components/collaboration/DragGhostCard';
import { KanbanColumn } from './KanbanColumn';
import { CreateColumnButton } from './CreateColumnButton';
import { TagManagementDialog } from './TagManagementDialog';
import { ThemeToggle } from '@/components/ThemeToggle';
import { useToast } from '@/hooks/use-toast';
import confetti from 'canvas-confetti';
import { UnifiedScrollArea } from '@/components/ui/unified-scroll-area';
import { ScrollBar } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Archive, EyeOff, Tag, Users } from 'lucide-react';
import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import MobileKanbanBoard from '@/components/mobile/MobileKanbanBoard';
import { BoardPresenceAvatars } from './BoardPresenceAvatars';
import { useAuth } from '@/hooks/useAuth';
import { CollaborationSidebar } from '@/components/collaboration/CollaborationSidebar';
import { ConnectionHealthIndicator } from '@/components/collaboration/ConnectionHealthIndicator';
import { TimeSync } from '@/utils/timeSync';
import { useLiveSync } from '@/hooks/useLiveSync';

// Inner component that uses drag state context
const KanbanBoardContent = () => {
  const { 
    columns, 
    cards, 
    updateCardPosition, 
    updateColumnPosition, 
    currentBoardId,
    columnsLoading,
    cardsLoading,
    getTagName,
    getTagColor,
    showArchivedCards,
    setShowArchivedCards,
    canEditCards,
    boards,
    // Animation coordination
    optimisticUpdateState,
    registerOptimisticUpdate,
  } = useBoardContext();

  const { dragState, startDrag, updateDrag, endDrag, connectionHealth, forceCleanupAllDrags } = useDragStateContext();
  const { toast } = useToast();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCollaborationSidebar, setShowCollaborationSidebar] = useState(false);
  const [dropTargetState, setDropTargetState] = useState<{ [columnId: string]: boolean }>({});

  // Add keyboard navigation for horizontal scrolling
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!scrollRef.current) return;

      // Check if card is being dragged or if user is typing in input/textarea
      const isDragging = document.querySelector('[data-rbd-drag-handle-dragging-id]') !== null;
      if (isDragging) return;

      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.contentEditable === 'true') {
        return;
      }

      const scrollAmount = 200;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          scrollRef.current.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
          break;
        case 'ArrowRight':
          e.preventDefault();
          scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
          break;
        case 'Home':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
          }
          break;
        case 'End':
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            scrollRef.current.scrollTo({ left: scrollRef.current.scrollWidth, behavior: 'smooth' });
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // **Optimized remote card animation management with stable refs**
  const remoteUpdateHandlersRef = useRef<Map<string, (updateFn: () => void) => void>>(new Map());
  const registeredCardsRef = useRef<Set<string>>(new Set());

  // Presence/user
  const { user } = useAuth();

  // Use board presence - fix the hook call to match the context signature
  const { presenceUsers: boardPresenceUsers, isConnected: boardPresenceConnected } = useBoardPresence();

  // **Enhanced live sync with animation coordination**
  const handleRemoteUpdate = useCallback((payload: any) => {
    if (!payload.new || payload.eventType === 'DELETE') return;
    
    const updatedCard = payload.new;
    
    // Skip updates from the current user to avoid conflicts with local drags
    if (updatedCard.last_edited_by === user?.id) {
      return;
    }
    
    // Check if card has active optimistic update
    const cardId = updatedCard.id;
    const optimisticTimestamp = optimisticUpdateState.get(cardId);
    
    if (optimisticTimestamp) {
      const timeSinceOptimistic = Date.now() - optimisticTimestamp;
      if (timeSinceOptimistic < 1000) { // Within settling period
        return; // Let BoardContext handle the debouncing
      }
    }
    
    // Trigger animation for remote card updates
    const handler = remoteUpdateHandlersRef.current.get(cardId);
    
    if (handler) {
      handler(() => {
        // The actual DOM update will happen through the normal React flow
      });
    }
  }, [user?.id, optimisticUpdateState]);

  const { isConnected: liveSyncConnected } = useLiveSync({
    boardId: currentBoardId || '',
    onUpdate: handleRemoteUpdate,
    enabled: !!currentBoardId
  });

  // **Card animation registration with proper cleanup**
  const registerCardForAnimation = useCallback((cardId: string, handler: (updateFn: () => void) => void) => {
    if (!registeredCardsRef.current.has(cardId)) {
      console.log('📝 [KANBAN] Registering card animation handler:', cardId);
      registeredCardsRef.current.add(cardId);
    }
    remoteUpdateHandlersRef.current.set(cardId, handler);
  }, []);

  const unregisterCardForAnimation = useCallback((cardId: string) => {
    if (registeredCardsRef.current.has(cardId)) {
      console.log('🗑️ [KANBAN] Unregistering card animation handler:', cardId);
      registeredCardsRef.current.delete(cardId);
    }
    remoteUpdateHandlersRef.current.delete(cardId);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      remoteUpdateHandlersRef.current.clear();
      registeredCardsRef.current.clear();
    };
  }, []);

  // Clear all drop targets
  const clearAllDropTargets = useCallback(() => {
    setDropTargetState({});
  }, []);

  // Enhanced connection health monitoring
  useEffect(() => {
    if (connectionHealth === 'disconnected') {
      console.log('🔌 Connection lost - forcing cleanup of all drag artifacts');
      forceCleanupAllDrags();
      clearAllDropTargets();
    }
  }, [connectionHealth, forceCleanupAllDrags, clearAllDropTargets]);

  // Enhanced filtered cards with proper memoization
    const filteredCards = useMemo(() => {
        if (!cards || !Array.isArray(cards)) return [];

        if (!searchQuery.trim()) return [...cards];

        const searchLower = searchQuery.toLowerCase();

        return cards.filter(card => {
            const matchTitle = card.title?.toLowerCase().includes(searchLower);
            const matchDescription = card.description?.toLowerCase().includes(searchLower);
            const matchCategory = card.category?.toLowerCase().includes(searchLower);
            const matchTags = Array.isArray(card.tags)
                ? card.tags.some(tagId => {
                    const tagName = getTagName(tagId);
                    return tagName?.toLowerCase().includes(searchLower);
                })
                : false;

            return matchTitle || matchDescription || matchCategory || matchTags;
        });
    }, [cards, searchQuery, getTagName]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  useEffect(() => {
    setSearchQuery('');
  }, [currentBoardId]);

  // Count archived cards
  const archivedCardCount = cards?.filter(card => card.archived).length || 0;

  // Enhanced drag handlers with optimistic update coordination - removed global touch event handling
  const handleDragStart = useCallback((start: any) => {
    const { draggableId, source } = start;
    
    if (start.type === 'column') return; // Don't track column drags
    
    const sourceColumnId = source.droppableId;
    
    // Clear any existing drop targets immediately
    clearAllDropTargets();
    
    // Start tracking this drag
    startDrag(draggableId, sourceColumnId, { x: 0, y: 0 });
  }, [startDrag, clearAllDropTargets]);

  const handleDragUpdate = useCallback((update: any) => {
    const { draggableId, destination } = update;
    
    if (update.type === 'column') return; // Don't track column drags
    
    const targetColumnId = destination?.droppableId || null;
    
    // Update drop target state with immediate feedback
    setDropTargetState(prev => {
      const newState = { ...prev };
      
      // Clear all previous drop targets
      Object.keys(newState).forEach(key => {
        newState[key] = false;
      });
      
      // Set current drop target if over a column
      if (targetColumnId) {
        newState[targetColumnId] = true;
      }
      
      return newState;
    });
    
    // Update drag position
    updateDrag(draggableId, targetColumnId, { x: 0, y: 0 });
  }, [updateDrag]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId, type } = result;
    
    // IMMEDIATE CLEANUP - Clear drag state and drop targets instantly
    const draggedCardId = draggableId;
    
    // Force clear drop targets immediately
    clearAllDropTargets();
    
    // End drag tracking for cards - immediate cleanup
    if (type !== 'column' && draggedCardId) {
      endDrag(draggedCardId, destination?.droppableId);
    }

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Enhanced column reordering with proper position management
    if (type === 'column') {
      if (!columns) return;
      
      console.log('Column drag:', { source, destination, draggableId });
      
      // Create a properly sorted array of columns
      const sortedColumns = [...columns].sort((a, b) => a.position - b.position);
      
      // Remove the dragged column
      const [movedColumn] = sortedColumns.splice(source.index, 1);
      
      // Insert at new position
      sortedColumns.splice(destination.index, 0, movedColumn);
      
      // Update positions for all affected columns
      sortedColumns.forEach((col, index) => {
        if (col.position !== index) {
          updateColumnPosition({ columnId: col.id, newPosition: index });
        }
      });
      
      return;
    }

    // Enhanced card moving with optimistic update registration
    const sourceColumn = columns?.find(col => col.id === source.droppableId);
    const destColumn = columns?.find(col => col.id === destination.droppableId);

    if (!sourceColumn || !destColumn) return;

    // Get cards in destination column for proper position calculation
    const destColumnCards = filteredCards
      .filter(card => card.column_id === destination.droppableId)
      .sort((a, b) => a.position - b.position);

    // Calculate the new position more accurately
    let newPosition = destination.index;
    
    // If moving within the same column, account for the card being removed
    if (source.droppableId === destination.droppableId) {
      // Moving within same column - adjust for removed card
      if (source.index < destination.index) {
        newPosition = destination.index;
      } else {
        newPosition = destination.index;
      }
    } else {
      // Moving to different column
      newPosition = destination.index;
    }

    // Completion celebration
    if (destColumn.is_completed_column && !sourceColumn.is_completed_column) {
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6']
      });
      
      toast({
        title: "🎉 Task Completed!",
        description: "Great job finishing this task!"
      });
    }

    // Register optimistic update before mutation for animation coordination
    registerOptimisticUpdate(draggableId);

    // Update card position with enhanced parameters
    updateCardPosition({
      cardId: draggableId,
      newColumnId: destination.droppableId,
      newPosition: newPosition
    });
  };

  // Balanced ghost card filtering with improved grace periods
  const otherUserDrags = useMemo(() => {
    const allDrags = Object.values(dragState).flat();
    
    const filtered = allDrags.filter(drag => {
      // Basic filters
      if (drag.user_id === user?.id || !drag.is_active) {
        return false;
      }
      
      // Balanced staleness detection - 2000ms for initial appearance
      if (drag.updated_at) {
        const age = TimeSync.getAge(drag.updated_at);
        const isStale = age > 2000; // Increased from 500ms to 2000ms
        if (isStale) {
          return false;
        }
      }
      
      // Grace period for position - allow drags without position for 500ms
      if (!drag.drag_position) {
        if (drag.updated_at) {
          const age = TimeSync.getAge(drag.updated_at);
          if (age > 500) { // Only filter if missing position for more than 500ms
            return false;
          }
        }
      }
      
      return true;
    });
    
    return filtered;
  }, [dragState, user?.id]);

  // Show loading state
  if (!currentBoardId || columnsLoading || cardsLoading || !columns) {
    return (
      <div className="h-full">
        <div className="flex justify-between items-center p-4 border-b bg-background">
          <h2 className="text-xl font-semibold">Kanban Board</h2>
          <div className="flex items-center gap-2">
            <TagManagementDialog>
              <Button variant="outline" size="sm">
                <Tag className="h-4 w-4 mr-2" />
                Manage Tags
              </Button>
            </TagManagementDialog>
            <ThemeToggle />
          </div>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex relative">
      {/* Enhanced ghost cards with balanced positioning and improved failsafe cleanup */}
      {otherUserDrags.map((dragUser) => {
        const draggedCard = cards?.find(card => card.id === dragUser.card_id);
        if (!draggedCard) {
          return null;
        }
        
        return (
          <DragGhostCard
            key={`${dragUser.user_id}-${dragUser.card_id}`}
            card={draggedCard}
            dragUser={dragUser}
            position={dragUser.drag_position || { x: 0, y: 0 }} // Fallback position
            isOverColumn={!!dragUser.target_column_id}
          />
        );
      })}

      {/* Main content area */}
      <div className={cn(
        "flex-1 flex flex-col transition-all duration-300 overflow-hidden",
        showCollaborationSidebar ? "mr-80" : ""
      )}>
        {/* BOARD HEADER */}
        <div className="flex justify-between items-center p-4 border-b bg-background flex-shrink-0">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-semibold">Kanban Board</h2>
            {/* Show board users present - filter out card-level presence */}
            <BoardPresenceAvatars 
              users={boardPresenceUsers.filter(user => !user.card_id).map(user => ({
                user_id: user.user_id,
                user_name: user.user_name || '',
                joined_at: user.joined_at || new Date().toISOString(),
                role: user.role === "admin" ? "admin" : user.role as "viewer" | "editor" | "admin",
                status: user.status || "viewing",
                updated_at: user.updated_at || new Date().toISOString(),
              }))} 
              currentUserId={user?.id || ''} 
            />
            
            {/* Enhanced connection health indicator */}
            <ConnectionHealthIndicator 
              showLabel 
              size="sm" 
            />
            
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search cards..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10 w-64 h-9 text-sm"
              />
            </div>
            
            {searchQuery.trim() && (
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {filteredCards.length} card{filteredCards.length !== 1 ? 's' : ''} found
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Archive toggle button */}
            <Button
              variant={showArchivedCards ? "default" : "outline"}
              size="sm"
              onClick={() => setShowArchivedCards(!showArchivedCards)}
              className="h-9"
            >
              {showArchivedCards ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  Hide Archived
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4 mr-2" />
                  Show Archived {archivedCardCount > 0 && `(${archivedCardCount})`}
                </>
              )}
            </Button>
            
            {/* Collaboration toggle button */}
            <Button
              variant={showCollaborationSidebar ? "default" : "outline"}
              size="sm"
              onClick={() => setShowCollaborationSidebar(!showCollaborationSidebar)}
              className="h-9"
            >
              <Users className="h-4 w-4 mr-2" />
              Collaborate
            </Button>
            
            {/* Tag Management Button */}
            <TagManagementDialog>
              <Button variant="outline" size="sm">
                <Tag className="h-4 w-4 mr-2" />
                Manage Tags
              </Button>
            </TagManagementDialog>
            
            <ThemeToggle />
          </div>
        </div>
        
        {/* Kanban content */}
        <div 
          ref={scrollRef}
          className="flex-1 w-full overflow-x-auto overflow-y-hidden" 
          style={{ height: 'calc(100vh - 8rem)' }}>
          <DragDropContext 
            onDragStart={handleDragStart}
            onDragUpdate={handleDragUpdate}
            onDragEnd={handleDragEnd}
          >
            <Droppable droppableId="board" type="column" direction="horizontal">
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={cn(
                    "flex gap-6 p-6 bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 transition-colors",
                    snapshot.isDraggingOver && "bg-blue-50/50 dark:bg-blue-950/50"
                  )}
                  style={{ 
                    minWidth: 'max-content',
                    height: '100%'
                  }}
                >
                  {columns
                    ?.sort((a, b) => a.position - b.position)
                    .map((column, index) => (
                      <Draggable key={column.id} draggableId={column.id} index={index}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={cn(
                              "transition-transform",
                              snapshot.isDragging && "rotate-2 shadow-lg scale-105 z-50"
                            )}
                          >
                            <KanbanColumn
                              column={column}
                              cards={filteredCards?.filter(card => 
                                card.column_id === column.id && (showArchivedCards || !card.archived)
                              ) || []}
                              dragHandleProps={provided.dragHandleProps}
                              onRegisterCardForAnimation={registerCardForAnimation}
                              onUnregisterCardForAnimation={unregisterCardForAnimation}
                            />
                          </div>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                  <CreateColumnButton />
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>

      {/* Collaboration Sidebar */}
      {showCollaborationSidebar && (
        <CollaborationSidebar 
          isOpen={showCollaborationSidebar}
          onClose={() => setShowCollaborationSidebar(false)} 
        />
      )}
    </div>
  );
};

const KanbanBoard = () => {
  const isMobile = useIsMobile();
  const { currentBoardId } = useBoardContext();

  if (isMobile) {
    return <MobileKanbanBoard />;
  }

  if (!currentBoardId) {
    return <div>No board selected</div>;
  }

  return (
    <BoardPresenceProvider boardId={currentBoardId}>
      <CardPresenceProvider boardId={currentBoardId}>
        <DragStateProvider boardId={currentBoardId}>
          <KanbanBoardContent />
        </DragStateProvider>
      </CardPresenceProvider>
    </BoardPresenceProvider>
  );
};

export default KanbanBoard;
